import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
// import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MoreIcon from '@mui/icons-material/MoreVert';
// import { theme } from '../theme'
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import { AppGlobalContext } from '../App';
import TEXT from './text';
import { useSelector } from 'react-redux';
import NotificationBadge from './NotificationBadge';

function progressCircle(progressCircleState) {

  switch (progressCircleState) {
    case 'success':
      return <Typography>Success!</Typography>
    case 'progress':
      return <Fade
        in={progressCircleState === 'progress'}
        style={{
          transitionDelay: progressCircleState === 'progress' ? '800ms' : '0ms',
        }}
        unmountOnExit
      >
        <CircularProgress
          color='background'
          size={30} />
      </Fade>
    default:
      return <></>
  }

}

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

/**
 * Renders a custom app bar component.
 *
 * @startuml
 * 
 * class CustomAppBar {
 *   - anchorEl: React.useState
 *   - mobileMoreAnchorEl: React.useState
 *   - isMenuOpen: Boolean
 *   - isMobileMenuOpen: Boolean
 *   + handleProfileMenuOpen(event: Event): void
 *   + handleMobileMenuClose(): void
 *   + handleMenuClose(): void
 *   + handleMobileMenuOpen(event: Event): void
 *   - menuId: string
 *   - renderMenu: JSX.Element
 *   - query: React.useState
 *   - mobileMenuId: string
 *   - renderMobileMenu: JSX.Element
 * }
 * 
 * @enduml
 * 
 * @param {Object} props - The props object containing the component's properties.
 * @return {JSX.Element} The rendered custom app bar component.
 */
export default function CustomAppBar(props) {
  const language = useSelector((state) => state.App_Settings.language);
  const profileImage = useSelector((state) => state.UserMetaData.profileImage);
  const fullName = useSelector((state) => state.UserMetaData.fullName);
  const { rooms } = useSelector(state => state.Chat);
  const AppGlobalContxt = React.useContext(AppGlobalContext);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);

  // Calculate total unread messages from rooms array
  const totalUnreadMessages = rooms?.reduce((sum, room) => sum + (room.unread_count || 0), 0) || 0;

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => {handleMenuClose(); AppGlobalContxt.Body.setPageName("Profile");}}>
        <IconButton
          size="large"
          aria-label="profile"
          color="inherit"
        >
          <PersonIcon />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
      <MenuItem onClick={handleMenuClose}>
        <IconButton
          size="large"
          aria-label="settings"
          color="inherit"
        >
          <SettingsIcon />
        </IconButton>
        <p>Settings</p>
      </MenuItem>
      <MenuItem onClick={() => {handleMenuClose(); AppGlobalContxt.Body.setPageName("Login");}}>
        <IconButton
          size="large"
          aria-label="logout"
          color="inherit"
        >
          <LogoutIcon />
        </IconButton>
        <p>Logout</p>
      </MenuItem>
    </Menu>
  );

  const [query, setQuery] = React.useState('progress')
  const mobileMenuId = 'primary-search-account-menu-mobile';
  // const progressCircleState = React.useContext(progressCircleContext)
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={()=>{handleMenuClose();AppGlobalContxt.Body.setPageName("Login");}}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
        <LogoutIcon/>
        </IconButton>
        <p>Logout</p>
      </MenuItem>

    </Menu>
  );

  return (
    <Box hidden={props.hidden} sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton onClick={props.drawerOpener}
            size="large"
            edge="start"
            color="inherit"
            aria-label="open drawer"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{/*{ display: { xs: 'none', sm: 'block' } }*/ }}
          >
            { /*to make the page name always visible*/}
            {console.log("AppBar.jsx: props.title", props.title)}
            {TEXT[props.title][language]}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'flex', md: 'flex' } }}>
           { progressCircle( props.progressCircleState)}
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>{/*TODO : enable notification and profile */}
            {/* Messages Icon - Navigate to Chat */}
            <IconButton 
              size="large" 
              aria-label={`show ${totalUnreadMessages} new messages`} 
              color="inherit"
              onClick={() => AppGlobalContxt.Body.setPageName("Chat")}
            >
              <Badge badgeContent={totalUnreadMessages} color="error">
                <MailIcon />
              </Badge>
            </IconButton>
            {/* Notifications Badge */}
            <NotificationBadge />
            {/* Profile Image */}
            <IconButton
              onClick={() => AppGlobalContxt.Body.setPageName("Profile")}
              color="inherit"
              sx={{ padding: 0.5 }}
              aria-label="go to profile"
            >
              {profileImage ? (
                <Box
                  component="img"
                  src={profileImage}
                  alt={fullName || 'User'}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid white',
                    cursor: 'pointer'
                  }}
                />
              ) : (
                <AccountCircle sx={{ fontSize: 40 }} />
              )}
            </IconButton>
            {/* Dropdown Menu Icon */}
            <IconButton
              size="large"
              edge="end"
              aria-label="account menu"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            {/* Messages Icon - Mobile */}
            <IconButton 
              size="large" 
              aria-label={`show ${totalUnreadMessages} new messages`} 
              color="inherit"
              onClick={() => AppGlobalContxt.Body.setPageName("Chat")}
            >
              <Badge badgeContent={totalUnreadMessages} color="error">
                <MailIcon />
              </Badge>
            </IconButton>
            {/* Notifications Icon - Mobile */}
            <IconButton
              size="large"
              aria-label="show notifications"
              color="inherit"
            >
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            {/* Profile Image - Always Visible */}
            <IconButton
              onClick={() => AppGlobalContxt.Body.setPageName("Profile")}
              color="inherit"
              sx={{ padding: 0.5 }}
              aria-label="go to profile"
            >
              {profileImage ? (
                <Box
                  component="img"
                  src={profileImage}
                  alt={fullName || 'User'}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid white',
                    cursor: 'pointer'
                  }}
                />
              ) : (
                <AccountCircle sx={{ fontSize: 36 }} />
              )}
            </IconButton>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
  );
}